import { useEffect, useMemo, useState } from 'react';
import StickyActions from '@/components/common/StickyActions.jsx';
import Field from '@/components/common/Field.jsx';
import ProvinciaSelect from '@/components/geo/ProvinciaSelect.jsx';
import LocalidadSelect from '@/components/geo/LocalidadSelect.jsx';

const condicionIvaOptions = [
  { value: 'RI', label: 'Responsable Inscripto' },
  { value: 'MT', label: 'Monotributo' },
  { value: 'EX', label: 'Exento' },
  { value: 'CF', label: 'Consumidor Final' },
  { value: 'RNI', label: 'Responsable No Inscripto' },
];

const defaultForm = {
  razon_social: '',
  cuit: '',
  condicion_iva: 'RI',
  contacto_nombre: '',
  contacto_tel: '',
  contacto_email: '',
  direccion: '',
  localidad_id: '',
  provincia_id: '',
  observaciones: '',
  estado: 'ACTIVO',
};

function formatCuit(value) {
  const digits = value.replace(/[^\d]/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2, 10)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

function validateField(name, value) {
  switch (name) {
    case 'razon_social':
      if (!value || !value.trim()) {
        return 'La razón social es obligatoria.';
      }
      if (value.trim().length < 3) {
        return 'La razón social debe tener al menos 3 caracteres.';
      }
      return '';
    case 'cuit': {
      const digits = value.replace(/[^\d]/g, '');
      if (digits.length !== 11) {
        return 'Ingresá un CUIT válido (11 dígitos).';
      }
      return '';
    }
    case 'condicion_iva':
      return value ? '' : 'Seleccioná una condición de IVA.';
    case 'contacto_nombre':
      return value && value.trim().length >= 3 ? '' : 'El nombre de contacto es obligatorio.';
    case 'contacto_tel':
      return value && value.trim().length >= 6 ? '' : 'El teléfono de contacto es obligatorio.';
    case 'contacto_email':
      if (!value) return 'El correo de contacto es obligatorio.';
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Correo electrónico inválido.';
    case 'direccion':
      return value && value.trim().length >= 4 ? '' : 'La dirección es obligatoria.';
    case 'provincia_id':
      return value ? '' : 'La provincia es obligatoria.';
    case 'localidad_id':
      return value ? '' : 'La localidad es obligatoria.';
    case 'observaciones':
      return value && value.trim().length >= 3 ? '' : 'Las observaciones son obligatorias.';
    default:
      return '';
  }
}

function mergeInitialValues(initialValues) {
  if (!initialValues) {
    return defaultForm;
  }
  return {
    ...defaultForm,
    ...initialValues,
  };
}

export default function ProveedorForm({
  initialValues,
  onSubmit,
  onCancel,
  submitLabel = 'Guardar',
  cancelLabel = 'Cancelar',
  loading,
  showStickyActions = true,
}) {
  const [form, setForm] = useState(() => mergeInitialValues(initialValues));
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    setForm(mergeInitialValues(initialValues));
    setErrors({});
    setTouched({});
  }, [initialValues]);

  const isValid = useMemo(() => {
    return (
      !Object.values(errors).some(Boolean) &&
      form.razon_social.trim() &&
      form.cuit.replace(/[^\d]/g, '').length === 11 &&
      form.condicion_iva &&
      form.contacto_nombre.trim() &&
      form.contacto_tel.trim() &&
      form.contacto_email.trim() &&
      form.direccion.trim() &&
      form.localidad_id &&
      form.provincia_id &&
      form.observaciones.trim()
    );
  }, [errors, form]);

  const handleFieldChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = name => {
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, form[name]) }));
  };

  const handleSubmit = event => {
    event.preventDefault();
    const validation = {
      razon_social: validateField('razon_social', form.razon_social),
      cuit: validateField('cuit', form.cuit),
      condicion_iva: validateField('condicion_iva', form.condicion_iva),
      contacto_nombre: validateField('contacto_nombre', form.contacto_nombre),
      contacto_tel: validateField('contacto_tel', form.contacto_tel),
      contacto_email: validateField('contacto_email', form.contacto_email),
      direccion: validateField('direccion', form.direccion),
      localidad_id: validateField('localidad_id', form.localidad_id),
      provincia_id: validateField('provincia_id', form.provincia_id),
      observaciones: validateField('observaciones', form.observaciones),
    };
    setErrors(validation);
    setTouched({
      razon_social: true,
      cuit: true,
      condicion_iva: true,
      contacto_nombre: true,
      contacto_tel: true,
      contacto_email: true,
      direccion: true,
      localidad_id: true,
      provincia_id: true,
      observaciones: true,
    });
    const hasError = Object.values(validation).some(Boolean);
    if (!hasError && onSubmit) {
      onSubmit({
        ...form,
        cuit: form.cuit.replace(/[^\d]/g, '').replace(/(\d{2})(\d{8})(\d{1})/, '$1-$2-$3'),
      });
    }
  };

  return (
    <form className="flex flex-col gap-8" onSubmit={handleSubmit} autoComplete="off">
      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <Field label="Razón social" htmlFor="razon_social" required error={errors.razon_social}>
              <input
                id="razon_social"
                type="text"
                autoComplete="new-password"
                value={form.razon_social}
                onChange={event => handleFieldChange('razon_social', event.target.value)}
                onBlur={() => handleBlur('razon_social')}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="CUIT" htmlFor="cuit" required error={errors.cuit}>
              <input
                id="cuit"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={form.cuit}
                onChange={event => handleFieldChange('cuit', formatCuit(event.target.value))}
                onBlur={() => handleBlur('cuit')}
                placeholder="##-########-#"
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Condición frente al IVA" htmlFor="condicion_iva" required error={errors.condicion_iva}>
              <select
                id="condicion_iva"
                value={form.condicion_iva}
                onChange={event => handleFieldChange('condicion_iva', event.target.value)}
                onBlur={() => handleBlur('condicion_iva')}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {condicionIvaOptions.map(option => (
                  <option key={option.value} value={option.value} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3 flex items-end">
            <Field label="Activo">
              <button
                type="button"
                role="switch"
                aria-checked={form.estado === 'ACTIVO'}
                onClick={() =>
                  setForm(prev => ({
                    ...prev,
                    estado: prev.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO',
                  }))
                }
                className={`relative inline-flex h-10 w-20 items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                  form.estado === 'ACTIVO' ? 'bg-[rgb(var(--brand))]' : 'bg-[rgba(var(--muted),0.35)]'
                }`}
              >
                <span className="sr-only">Proveedor activo</span>
                <span
                  className={`${
                    form.estado === 'ACTIVO' ? 'translate-x-10' : 'translate-x-1'
                  } inline-flex h-8 w-8 items-center justify-center rounded-full bg-[rgb(var(--panel))] text-xs font-semibold text-[rgb(var(--fg))] shadow-md transition`}
                >
                  {form.estado === 'ACTIVO' ? 'SI' : 'NO'}
                </span>
              </button>
            </Field>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Contacto</h2>
        <div className="mt-4 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <Field label="Nombre" htmlFor="contacto_nombre" required error={errors.contacto_nombre}>
              <input
                id="contacto_nombre"
                type="text"
                autoComplete="new-password"
                value={form.contacto_nombre}
                onChange={event => handleFieldChange('contacto_nombre', event.target.value)}
                onBlur={() => handleBlur('contacto_nombre')}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Teléfono" htmlFor="contacto_tel" required error={errors.contacto_tel}>
              <input
                id="contacto_tel"
                type="text"
                autoComplete="off"
                value={form.contacto_tel}
                onChange={event => handleFieldChange('contacto_tel', event.target.value)}
                onBlur={() => handleBlur('contacto_tel')}
                placeholder="+54 11 5555-5555"
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Correo" htmlFor="contacto_email" required error={errors.contacto_email}>
              <input
                id="contacto_email"
                type="email"
                autoComplete="off"
                value={form.contacto_email}
                onChange={event => handleFieldChange('contacto_email', event.target.value)}
                onBlur={() => handleBlur('contacto_email')}
                placeholder="contacto@empresa.com"
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Dirección</h2>
        <div className="mt-4 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <Field label="Dirección" htmlFor="direccion" required error={errors.direccion}>
              <input
                id="direccion"
                type="text"
                autoComplete="new-password"
                value={form.direccion}
                onChange={event => handleFieldChange('direccion', event.target.value)}
                onBlur={() => handleBlur('direccion')}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <ProvinciaSelect
              label="Provincia"
              value={form.provincia_id}
              onChange={value => {
                handleFieldChange('provincia_id', value);
                handleFieldChange('localidad_id', '');
              }}
              required
              error={touched.provincia_id ? errors.provincia_id : ''}
              onBlur={() => handleBlur('provincia_id')}
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <LocalidadSelect
              label="Localidad"
              provinciaId={form.provincia_id}
              value={form.localidad_id}
              onChange={value => handleFieldChange('localidad_id', value)}
              disabled={!form.provincia_id}
              required
              error={touched.localidad_id ? errors.localidad_id : ''}
              onBlur={() => handleBlur('localidad_id')}
            />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <Field label="Observaciones" htmlFor="observaciones" required error={errors.observaciones}>
          <textarea
            id="observaciones"
            rows={4}
            autoComplete="off"
            value={form.observaciones}
            onChange={event => handleFieldChange('observaciones', event.target.value)}
            onBlur={() => handleBlur('observaciones')}
            className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          />
        </Field>
      </section>

      {showStickyActions ? (
        <StickyActions
          onCancel={onCancel}
          onSubmit={handleSubmit}
          isSubmitting={loading}
          primaryLabel={submitLabel}
          secondaryLabel={cancelLabel}
        />
      ) : (
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={!isValid || loading}
            className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_28px_rgba(var(--shadow),0.16)] transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}
